import { Grid, Header, Segment } from "semantic-ui-react";
import React from "react";
import { MathUtils, StringUtils } from "utils";
import { useSelector } from "react-redux";
import { useController, useFieldArray, useFormContext } from "react-hook-form";

const CheckoutTotals = () => {
    const additionalTaxes = useSelector(state => state.order.additionalTaxes || 0);
    const paymentMethods = useSelector(
        (state) => state.payment.profile?.paymentProfiles || []
    );
    const { control } = useFormContext();
    const { field: termField } = useController({
        name: "term",
        control,
    });
    const [termValue] = (termField.value || "").split("/");
    const { field: paymentMethodField } = useController({
        control,
        name: 'payment_method'
    });
    const order = useSelector((state) => state.order.order);
    const { fields: cart } = useFieldArray({
        control,
        name: "cart",
        keyName: "key",
    });
    const paymentInfo = paymentMethods.find(
        (paymentMethod) =>
            paymentMethodField.value ===
            paymentMethod.customerPaymentProfileId
    );
    const { subtotal } = termValue === "demo" ? { subtotal: 0 } : cart
        .filter((item) => !Boolean(item.removed))
        .reduce(
            (a, item) => {
                const productDetails = order?.order_items?.find(
                    (quoteItem) =>
                        quoteItem.product_id === item.id &&
                        quoteItem.product_variation_id === item.variation_id
                );
                const price_data = Object.keys(productDetails.price_data).length ? productDetails.price_data : null;
                const price = (() => {
                    if (productDetails.dyl_product_name?.includes("_balance")) {
                        return item.amount || 0;
                    }
                    if (!price_data) {
                        return productDetails.item_price || 0;
                    }
                    const isOneTime =
                        !price_data?.model?.includes(
                            "recurring"
                        );
                    const pricingModel = price_data.model;
                    if (isOneTime) {
                        if (!pricingModel.includes("volume")) {
                            return pricingModel === "usage"
                                ? price_data.price.price
                                : price_data.price;
                        }
                        return (
                            price_data.price.find(
                                (range) =>
                                    item.quantity >= range.start &&
                                    item.quantity <=
                                    (range.end || Number.POSITIVE_INFINITY)
                            )?.price || 0
                        );
                    }
                    const price_schedule = price_data.price[item.pricing_schedule];
                    if (!pricingModel.includes("volume")) {
                        return (
                            productDetails?.prorate_amount || price_schedule?.price || 0
                        );
                    }
                    const frequency = price_schedule?.volumes || [];
                    const volumeTier = frequency.find(
                        (range) => item.quantity >= range.start &&
                            item.quantity <=
                            (range.end || Number.POSITIVE_INFINITY)
                    );
                    return (
                        productDetails?.prorate_amount || volumeTier?.price || 0
                    );
                })();

                const {
                    subtotal
                } = MathUtils.calculateItemTotals({
                    price,
                    additional_price:
                        productDetails.product_additional_price || 0,
                    quantity: item.quantity,
                    discount: item.discount || 0,
                    addons: productDetails.addon
                        ?.filter(
                            (addon) =>
                                item.addons.includes(addon.id) && !addon.unlinked
                        )
                        .map((addon) => ({
                            price: addon.current_price || 0,
                        })) || [],
                    taxes: productDetails.tax?.map((tax) => ({
                        amount: tax.current_amount || tax.amount,
                        percent: tax.tax_fee_percent,
                    })) || [],
                    fees: productDetails.fee?.map((fee) => ({
                        amount: fee.current_amount || fee.amount,
                        percent: fee.tax_fee_percent,
                    })) || [],
                });

                return {
                    subtotal: a.subtotal + subtotal,
                };
            },
            {
                subtotal: 0,
            }
        );

    const orderSubtotal = subtotal + additionalTaxes;
    const isUsingCreditCard = paymentInfo?.payment?.creditCard;
    const creditCardProcessingFee = orderSubtotal * (isUsingCreditCard ? 0.029 : 0);
    return (
        <Segment compact>
            <Grid divided="vertically" >
                <Grid.Row>
                    <Grid.Column>
                        <Header>Amount Due</Header>
                    </Grid.Column>
                </Grid.Row>
                <Grid.Row columns="2">
                    <Grid.Column>
                        <Header size="small">Order Subtotal</Header>
                    </Grid.Column>
                    <Grid.Column textAlign="right">
                        <Header color="primary" size="small">
                            $
                            {StringUtils.formatDecimal(
                                `${MathUtils.roundDecimals(orderSubtotal)}`
                            , true)}
                        </Header>
                    </Grid.Column>
                </Grid.Row>
                <Grid.Row columns="2">
                    <Grid.Column>
                        <Header size="small">Credit Card Processing Fee (2.9%)</Header>
                    </Grid.Column>
                    <Grid.Column textAlign="right">
                        <Header color="primary" size="small">
                            $
                            {StringUtils.formatDecimal(
                                `${MathUtils.roundDecimals(creditCardProcessingFee)}`
                            , true)}
                        </Header>
                    </Grid.Column>
                </Grid.Row>
                <Grid.Row columns="2">
                    <Grid.Column>
                        <Header size="small">Total Amount Charged</Header>
                    </Grid.Column>
                    <Grid.Column textAlign="right">
                        <Header color="primary" size="small">
                            $
                            {StringUtils.formatDecimal(
                                `${MathUtils.roundDecimals(orderSubtotal + creditCardProcessingFee)}`
                            , true)}
                        </Header>
                    </Grid.Column>
                </Grid.Row>
            </Grid>
        </Segment>
    );
};

export default CheckoutTotals;
